import dayjs from "dayjs";
import type { DateRange } from "../constants/config/filter";

// `value` is the preset id the Last Learned dropdown has always used; `aliases`
// are the extra short spellings accepted in a URL. Shaped so RANGE_DEFINITIONS
// is directly assignable to DatePreset[].
export type RangeDefinition = {
  value: string;
  label: string;
  aliases: string[];
  getRange: () => [Date, Date];
};

export const RANGE_DEFINITIONS: RangeDefinition[] = [
  {
    value: "today",
    label: "Today",
    aliases: ["0d"],
    getRange: () => {
      const today = dayjs();
      return [today.toDate(), today.toDate()];
    },
  },
  {
    value: "yesterday",
    label: "Yesterday",
    aliases: [],
    getRange: () => {
      const today = dayjs();
      return [
        today.subtract(1, "day").toDate(),
        today.subtract(1, "day").toDate(),
      ];
    },
  },
  {
    value: "last7days",
    label: "Last 7 days",
    aliases: ["7d"],
    getRange: () => {
      const today = dayjs();
      return [today.subtract(7, "day").toDate(), today.toDate()];
    },
  },
  {
    value: "last15days",
    label: "Last 15 days",
    aliases: ["15d"],
    getRange: () => {
      const today = dayjs();
      return [today.subtract(15, "day").toDate(), today.toDate()];
    },
  },
  {
    value: "last30days",
    label: "Last 30 days",
    aliases: ["30d"],
    getRange: () => {
      const today = dayjs();
      return [today.subtract(30, "day").toDate(), today.toDate()];
    },
  },
  {
    value: "lastMonth",
    label: "Last month",
    aliases: [],
    getRange: () => {
      const today = dayjs();
      return [
        today.subtract(1, "month").startOf("month").toDate(),
        today.subtract(1, "month").endOf("month").toDate(),
      ];
    },
  },
];

export const DEFAULT_RANGE_TOKEN = "7d";

const toDateRange = (definition: RangeDefinition): DateRange => {
  const [from, to] = definition.getRange();
  return {
    from: dayjs(from).format("YYYY-MM-DD"),
    to: dayjs(to).format("YYYY-MM-DD"),
    preset: definition.value,
  };
};

/**
 * Human label for a date range: the preset name when the range is a preset,
 * otherwise the literal span. A fixed range is never named after a preset its
 * dates happen to match today — tomorrow it no longer would, and the label
 * would have claimed a "Last 7 days" the link does not keep.
 */
export const formatDateRangeLabel = (range: DateRange): string => {
  const from = dayjs(range.from);
  const to = dayjs(range.to);

  const preset = RANGE_DEFINITIONS.find(
    (definition) => definition.value === range.preset,
  );
  if (preset) return preset.label;

  const fromText = from.format("M/D/YYYY");
  const toText = to.format("M/D/YYYY");
  return fromText === toText ? fromText : `${fromText} - ${toText}`;
};

export const resolveRangeToken = (raw: string | null): DateRange | null => {
  if (!raw) return null;
  const token = raw.trim().toLowerCase();
  if (!token) return null;

  const match = RANGE_DEFINITIONS.find(
    (definition) =>
      definition.value.toLowerCase() === token ||
      definition.aliases.some((alias) => alias.toLowerCase() === token),
  );
  return match ? toDateRange(match) : null;
};
